// SellMedicineScreen.jsx - FIXED VERSION (No hooks in map)
// This version fixes the React hooks error
// FIND AND REPLACE: Replace your entire SellMedicineScreen.jsx with this code

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import MedicineSearch from "../../components/pharmacistComponents/MedicineSearch";
import LowStockList from "../../components/pharmacistComponents/LowStockList";
import { 
  createSale,
  fetchPrescriptionByMRCode
} from "../../Redux/actions/pharmacistActions/pharmacistMedicineActions";
import { showToast } from "../../components/ToastMessage.jsx";
import { fetchTopSelling } from "../../Redux/actions/adminActions/medicineActions.js";
import printReceipt from "../../components/pharmacistComponents/PrintSlip.jsx";
import { useSanitizedInput } from "../../hooks/useSanitizedInput";
import { PRESCRIPTION_CLEAR } from "../../Redux/constants/pharmacistConstants/pharmacistConstants";

const SellMedicineScreen = () => {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.auth);
  const { topSelling } = useSelector((state) => state.adminMedicines);
  const { prescription } = useSelector((state) => state.pharmacy || {});

  const [cart, setCart] = useState([]);
  const [overallDiscount, handleDiscountChange, resetDiscount, setDiscountDirect] = useSanitizedInput('', 'number');
  const [pharmacistId, setPharmacistId] = useState("");
  const [topMedicines, setTopMedicines] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showLowStock, setShowLowStock] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const [mrCode, handleMRCodeChange, resetMRCode] = useSanitizedInput('', 'mrcode');
  const [searchingPrescription, setSearchingPrescription] = useState(false);
  const [prescriptionInfo, setPrescriptionInfo] = useState(null);
  
  // ✅ FIXED: State for unavailable/custom medicines and their prices
  const [unavailableMedicines, setUnavailableMedicines] = useState([]);
  const [showUnavailableModal, setShowUnavailableModal] = useState(false);
  const [customPrices, setCustomPrices] = useState({}); // ✅ Store prices in object instead of individual states

  useEffect(() => {
    if (userInfo)
      setPharmacistId(
        (userInfo.user && userInfo.user._id) || userInfo._id || ""
      );

    dispatch(fetchTopSelling());
    if (topSelling) setTopMedicines(topSelling || []);
  }, [dispatch, userInfo, topSelling]);

  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshKey((prev) => prev + 1);
      dispatch(fetchTopSelling());
    }, 30000);

    return () => clearInterval(interval);
  }, [dispatch]);

  const handleSearchPrescription = async () => {
    if (!mrCode.trim() || mrCode.trim().length < 4) {
      showToast({ type: "error", message: "Please enter a valid MR Code" });
      return;
    }

    setSearchingPrescription(true);
    
    try {
      const result = await dispatch(fetchPrescriptionByMRCode(mrCode.trim()));
      
      if (result.ok && result.data.found) {
        setPrescriptionInfo(result.data);
        
        const prescribedMeds = result.data.medicines || [];
        const newCartItems = [];
        const unavailable = [];
        let skippedCount = 0;
        
        for (const med of prescribedMeds) {
          if (cart.find(c => c.medicine === med._id || c.customName === med.medicineName)) {
            skippedCount++;
            continue;
          }
          
          if (med.isCustom && !med._id) {
            unavailable.push({
              id: `custom_${Date.now()}_${Math.random()}`, // ✅ Unique ID for each custom medicine
              medicineName: med.medicineName,
              company: med.company,
              dosage: med.dosage,
              frequency: med.frequency,
              duration: med.duration,
              route: med.route,
              instructions: med.instructions,
              isCustom: true
            });
            continue;
          }
          
          newCartItems.push({
            medicine: med._id,
            name: med.name || med.medicineName,
            priceAtSale: Number(med.price || 0),
            quantity: 1,
            discount: 0,
            stock: Number(med.stock || 0),
            isPrescribed: true,
            isOutOfStock: med.stock === 0,
            prescriptionDetails: {
              dosage: med.dosage,
              frequency: med.frequency,
              duration: med.duration,
              route: med.route,
              instructions: med.instructions
            }
          });
        }
        
        if (newCartItems.length > 0) {
          setCart(prev => [...prev, ...newCartItems]);
          const outOfStockCount = newCartItems.filter(item => item.isOutOfStock).length;
          showToast({ 
            type: "success", 
            message: `Added ${newCartItems.length} prescribed medicine(s) to cart${outOfStockCount > 0 ? ` (${outOfStockCount} out of stock)` : ''}` 
          });
        }
        
        if (unavailable.length > 0) {
          setUnavailableMedicines(unavailable);
          // ✅ Initialize prices object for custom medicines
          const initialPrices = {};
          unavailable.forEach(med => {
            initialPrices[med.id] = '';
          });
          setCustomPrices(initialPrices);
          setShowUnavailableModal(true);
        }
        
        if (newCartItems.length === 0 && unavailable.length === 0 && skippedCount > 0) {
          showToast({ 
            type: "info", 
            message: "All prescribed medicines are already in cart" 
          });
        }
      } else {
        showToast({ 
          type: "error", 
          message: result.error || "No prescription found for this MR Code" 
        });
        setPrescriptionInfo(null);
      }
    } catch (error) {
      console.error("Prescription search error:", error);
      showToast({ type: "error", message: "Failed to fetch prescription" });
    } finally {
      setSearchingPrescription(false);
    }
  };

  const handleClearPrescription = () => {
    resetMRCode();
    setPrescriptionInfo(null);
    setUnavailableMedicines([]);
    setCustomPrices({});
    dispatch({ type: PRESCRIPTION_CLEAR });
    setCart(prev => prev.filter(item => !item.isPrescribed && !item.isCustom));
    showToast({ type: "info", message: "Prescription cleared" });
  };

  const findCartItem = (medicineId) =>
    cart.find((c) => c.medicine === medicineId);

  const handleAdd = (med) => {
    if (!med) return;

    const existing = findCartItem(med._id);

    setCart((prev) => {
      if (existing) {
        return prev.map((p) =>
          p.medicine === med._id
            ? { ...p, quantity: Number(p.quantity) + 1 }
            : p
        );
      }
      return [
        ...prev,
        {
          medicine: med._id,
          name: med.name,
          priceAtSale: Number(med.price || 0),
          quantity: 1,
          discount: 0,
          stock: Number(med.stock || 0),
          isPrescribed: false,
          isOutOfStock: med.stock === 0,
        },
      ];
    });

    showToast({
      type: "success",
      message: `${med.name} added to cart`,
    });
  };

  // ✅ FIXED: Update custom price handler
  const handleCustomPriceChange = (medId, value) => {
    setCustomPrices(prev => ({
      ...prev,
      [medId]: value
    }));
  };

  const handleAddCustomMedicine = (customMed) => {
    const price = customPrices[customMed.id];
    
    if (!price || Number(price) <= 0) {
      showToast({ type: "error", message: "Please enter a valid price" });
      return;
    }

    const existing = cart.find(c => c.customName === customMed.medicineName);
    
    if (existing) {
      showToast({ type: "warning", message: "This custom medicine is already in cart" });
      return;
    }

    setCart(prev => [...prev, {
      medicine: customMed.id,
      name: customMed.medicineName,
      customName: customMed.medicineName,
      priceAtSale: Number(price),
      quantity: 1,
      discount: 0,
      stock: 0,
      isPrescribed: true,
      isCustom: true,
      prescriptionDetails: {
        dosage: customMed.dosage,
        frequency: customMed.frequency,
        duration: customMed.duration,
        route: customMed.route,
        instructions: customMed.instructions
      }
    }]);

    // Remove from unavailable list
    setUnavailableMedicines(prev => prev.filter(m => m.id !== customMed.id));
    
    // Close modal if no more custom medicines
    if (unavailableMedicines.length === 1) {
      setShowUnavailableModal(false);
    }

    showToast({ type: "success", message: `Custom medicine "${customMed.medicineName}" added to cart` });
  };

  const handleRemoveItem = (medicineId) => {
    setCart((prev) => prev.filter((item) => item.medicine !== medicineId));
    showToast({ type: "info", message: "Item removed" });
  };

  const resetEverything = () => {
    setCart([]);
    resetDiscount();
    resetMRCode();
    setPrescriptionInfo(null);
    setUnavailableMedicines([]);
    setCustomPrices({});
    dispatch({ type: PRESCRIPTION_CLEAR });
    setRefreshKey((prev) => prev + 1);
    dispatch(fetchTopSelling());
  };

  const handleCheckout = () => {
    if (cart.length === 0)
      return showToast({ type: "error", message: "Cart is empty" });
    
    const customWithoutPrice = cart.filter(item => item.isCustom && item.priceAtSale === 0);
    if (customWithoutPrice.length > 0) {
      showToast({ 
        type: "error", 
        message: "Please set price for all custom medicines before checkout" 
      });
      return;
    }
    
    setShowConfirmation(true);
  };

  const confirmCheckout = async () => {
    setShowConfirmation(false);

    const items = cart
      .filter(item => !item.isCustom)
      .map((it) => ({
        medicine: it.medicine,
        quantity: Number(it.quantity),
        discount: Number(it.discount || 0),
      }));

    if (items.length === 0) {
      showToast({ 
        type: "error", 
        message: "Cart contains only custom medicines. At least one database medicine is required for sale." 
      });
      return;
    }

    const payload = {
      items,
      overallDiscount: Number(overallDiscount || 0),
      paymentMethod: "cash",
    };

    try {
      const res = await dispatch(createSale(payload));
      if (res && res.ok) {
        await printReceipt(res.sale, pharmacistId);
        
        const customCount = cart.filter(item => item.isCustom).length;
        if (customCount > 0) {
          showToast({ 
            type: "info", 
            message: `Sale completed. Note: ${customCount} custom medicine(s) not recorded in system.` 
          });
        }
        
        resetEverything();
        showToast({ type: "success", message: "Sale completed successfully" });
      } else {
        showToast({
          type: "error",
          message: (res && res.error) || "Sale failed",
        });
      }
    } catch (err) {
      console.error(err);
      showToast({ type: "error", message: "Sale failed" });
    }
  };

  const totalQty = cart.reduce((s, it) => s + Number(it.quantity), 0);
  const subTotal = cart.reduce(
    (sum, it) => sum + Number(it.priceAtSale) * Number(it.quantity),
    0
  );
  const grandTotal = subTotal - Number(overallDiscount || 0);
  const prescribedCount = cart.filter(item => item.isPrescribed).length;
  const nonPrescribedCount = cart.length - prescribedCount;
  const customCount = cart.filter(item => item.isCustom).length;
  const outOfStockCount = cart.filter(item => item.isOutOfStock).length;

  return (
    <div className="container-fluid" style={{ height: "100vh", background: "#f8f9fa", padding: 0 }}>
      {/* ✅ FIXED: Unavailable/Custom Medicines Modal */}
      {showUnavailableModal && unavailableMedicines.length > 0 && (
        <div 
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999
          }}
          onClick={() => setShowUnavailableModal(false)}
        >
          <div 
            className="card"
            style={{
              maxWidth: "600px",
              width: "90%",
              maxHeight: "80vh",
              overflowY: "auto",
              borderRadius: "12px",
              boxShadow: "0 10px 40px rgba(0,0,0,0.2)"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="card-header bg-warning text-dark">
              <h5 className="mb-0">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                Custom/Unavailable Medicines
              </h5>
            </div>
            <div className="card-body">
              <p className="text-muted mb-3">
                The following medicines from the prescription are not available in the database. 
                You can add them manually with a custom price.
              </p>
              
              {unavailableMedicines.map((med) => (
                <div key={med.id} className="card mb-2" style={{border: "1px solid #ffc107"}}>
                  <div className="card-body p-3">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div style={{flex: 1}}>
                        <strong style={{fontSize: "1rem"}}>{med.medicineName}</strong>
                        {med.company && (
                          <div className="small text-muted">Company: {med.company}</div>
                        )}
                      </div>
                      <span className="badge bg-warning text-dark">Custom</span>
                    </div>
                    
                    <div className="small mb-2" style={{lineHeight: 1.8}}>
                      <div><strong>Dosage:</strong> {med.dosage}</div>
                      <div><strong>Frequency:</strong> {med.frequency}</div>
                      <div><strong>Duration:</strong> {med.duration}</div>
                      {med.route && <div><strong>Route:</strong> {med.route}</div>}
                      {med.instructions && <div><strong>Instructions:</strong> {med.instructions}</div>}
                    </div>
                    
                    <div className="d-flex gap-2 align-items-end">
                      <div style={{flex: 1}}>
                        <label className="form-label mb-1 small">Set Price (PKR)</label>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          placeholder="Enter price"
                          min="0"
                          step="0.01"
                          value={customPrices[med.id] || ''}
                          onChange={(e) => handleCustomPriceChange(med.id, e.target.value)}
                          style={{borderRadius: "6px"}}
                        />
                      </div>
                      <button
                        className="btn btn-success btn-sm"
                        onClick={() => handleAddCustomMedicine(med)}
                        style={{borderRadius: "6px", whiteSpace: "nowrap"}}
                      >
                        <i className="bi bi-plus-circle me-1"></i>
                        Add to Cart
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="card-footer text-end">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowUnavailableModal(false);
                  setUnavailableMedicines([]);
                  setCustomPrices({});
                }}
                style={{borderRadius: "8px"}}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div 
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999
          }}
          onClick={() => setShowConfirmation(false)}
        >
          <div 
            className="card"
            style={{
              maxWidth: "450px",
              width: "90%",
              borderRadius: "12px",
              boxShadow: "0 10px 40px rgba(0,0,0,0.2)"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="card-body p-4">
              <div className="text-center mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" fill="currentColor" viewBox="0 0 16 16" style={{color: "#667eea"}}>
                  <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                  <path d="M5.255 5.786a.237.237 0 0 0 .241.247h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 0 0 .25.246h.811a.25.25 0 0 0 .25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.977 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.75 2.286zm1.557 5.763c0 .533.425.927 1.01.927.609 0 1.028-.394 1.028-.927 0-.552-.42-.94-1.029-.94-.584 0-1.009.388-1.009.94z"/>
                </svg>
              </div>
              
              <h5 className="text-center fw-bold mb-3">Confirm Sale</h5>
              
              <div className="mb-3 p-3" style={{background: "#f8f9fa", borderRadius: "8px"}}>
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted">Total Items:</span>
                  <strong>{totalQty}</strong>
                </div>
                {prescribedCount > 0 && (
                  <>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted" style={{fontSize: "0.85rem"}}>
                        <i className="bi bi-prescription2 me-1"></i>Prescribed:
                      </span>
                      <span className="badge bg-success">{prescribedCount}</span>
                    </div>
                    {customCount > 0 && (
                      <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted" style={{fontSize: "0.85rem"}}>
                          <i className="bi bi-tag me-1"></i>Custom:
                        </span>
                        <span className="badge bg-warning text-dark">{customCount}</span>
                      </div>
                    )}
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted" style={{fontSize: "0.85rem"}}>
                        <i className="bi bi-cart-plus me-1"></i>Additional:
                      </span>
                      <span className="badge bg-info">{nonPrescribedCount}</span>
                    </div>
                  </>
                )}
                {outOfStockCount > 0 && (
                  <div className="alert alert-warning py-2 px-2 mb-2" style={{fontSize: "0.8rem"}}>
                    <i className="bi bi-exclamation-triangle me-1"></i>
                    {outOfStockCount} item(s) are out of stock
                  </div>
                )}
                {customCount > 0 && (
                  <div className="alert alert-info py-2 px-2 mb-2" style={{fontSize: "0.8rem"}}>
                    <i className="bi bi-info-circle me-1"></i>
                    {customCount} custom medicine(s) won't be recorded in inventory
                  </div>
                )}
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted">Subtotal:</span>
                  <strong>Rs {subTotal.toFixed(2)}</strong>
                </div>
                {Number(overallDiscount) > 0 && (
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Discount:</span>
                    <strong className="text-danger">- Rs {Number(overallDiscount).toFixed(2)}</strong>
                  </div>
                )}
                <hr className="my-2" />
                <div className="d-flex justify-content-between">
                  <span className="fw-semibold">Total Amount:</span>
                  <strong style={{color: "#667eea", fontSize: "1.25rem"}}>
                    Rs {grandTotal.toFixed(2)}
                  </strong>
                </div>
              </div>

              <p className="text-center text-muted mb-4" style={{fontSize: "0.9rem"}}>
                Are you sure you want to complete this sale?
              </p>

              <div className="d-flex gap-2">
                <button
                  className="btn btn-outline-secondary flex-fill"
                  onClick={() => setShowConfirmation(false)}
                  style={{borderRadius: "8px", padding: "0.6rem"}}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-success flex-fill fw-semibold"
                  onClick={confirmCheckout}
                  style={{borderRadius: "8px", padding: "0.6rem"}}
                >
                  Confirm Sale
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="row g-0" style={{ height: "calc(100vh - 80px)" }}>
        {/* LEFT SIDE - Products Area */}
        <div className="col-lg-8" style={{ height: "100%", overflow: "auto", padding: "1.5rem" }}>
          
          {/* MR-Code Prescription Search Section */}
          <div className="card mb-3" style={{ 
            border: "2px solid #667eea", 
            borderRadius: "12px", 
            boxShadow: "0 2px 8px rgba(102,126,234,0.1)" 
          }}>
            <div className="card-body p-3">
              <div className="d-flex align-items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16" style={{color: "#667eea"}} className="me-2">
                  <path d="M8 1a2.5 2.5 0 0 1 2.5 2.5V4h-5v-.5A2.5 2.5 0 0 1 8 1zm3.5 3v-.5a3.5 3.5 0 1 0-7 0V4H1v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4h-3.5zM2 5h12v9a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V5z"/>
                </svg>
                <h6 className="mb-0 fw-semibold" style={{color: "#667eea"}}>
                  Load Prescription by MR-Code
                </h6>
              </div>
              
              <div className="row g-2 align-items-end">
                <div className="col-md-6">
                  <label className="form-label mb-1" style={{fontSize: "0.85rem", fontWeight: 600}}>
                    Enter Patient's MR Code
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g., MR-00001"
                    value={mrCode}
                    onChange={handleMRCodeChange}
                    maxLength={50}
                    style={{
                      borderRadius: "8px",
                      border: "2px solid #e0e0e0",
                      padding: "0.6rem"
                    }}
                    onKeyPress={(e) => e.key === "Enter" && handleSearchPrescription()}
                  />
                </div>
                <div className="col-md-6">
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-primary flex-fill"
                      onClick={handleSearchPrescription}
                      disabled={!mrCode.trim() || searchingPrescription}
                      style={{
                        borderRadius: "8px",
                        padding: "0.6rem",
                        fontWeight: 600
                      }}
                    >
                      {searchingPrescription ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Searching...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-search me-1"></i>
                          Load Prescription
                        </>
                      )}
                    </button>
                    {prescriptionInfo && (
                      <button
                        className="btn btn-outline-secondary"
                        onClick={handleClearPrescription}
                        style={{borderRadius: "8px", padding: "0.6rem"}}
                        title="Clear prescription"
                      >
                        <i className="bi bi-x-circle"></i>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {prescriptionInfo && (
                <div className="alert alert-success mt-3 mb-0" style={{
                  borderRadius: "8px",
                  border: "1px solid #28a745"
                }}>
                  <div className="d-flex justify-content-between align-items-start">
                    <div style={{flex: 1}}>
                      <strong style={{fontSize: "0.95rem"}}>
                        <i className="bi bi-check-circle-fill me-2"></i>
                        Prescription Loaded Successfully
                      </strong>
                      <div className="small mt-2" style={{lineHeight: 1.6}}>
                        <div>
                          <strong>Patient:</strong> {prescriptionInfo.patient?.name} 
                          <code className="ms-2 bg-white px-2 py-1 rounded">{prescriptionInfo.patient?.mrCode}</code>
                        </div>
                        <div>
                          <strong>Doctor:</strong> Dr. {prescriptionInfo.doctor?.name}
                          {prescriptionInfo.doctor?.specialization && 
                            <span className="text-muted"> ({prescriptionInfo.doctor.specialization})</span>
                          }
                        </div>
                        <div>
                          <strong>Diagnosis:</strong> {prescriptionInfo.diagnosis}
                        </div>
                        <div>
                          <strong>Date:</strong> {new Date(prescriptionInfo.prescriptionDate).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <div className="d-flex flex-column gap-1 align-items-end">
                      <span className="badge bg-success" style={{
                        fontSize: "0.85rem",
                        padding: "0.5rem 0.75rem"
                      }}>
                        <i className="bi bi-prescription2 me-1"></i>
                        {prescribedCount} in cart
                      </span>
                      {unavailableMedicines.length > 0 && (
                        <button
                          className="btn btn-warning btn-sm"
                          onClick={() => setShowUnavailableModal(true)}
                          style={{fontSize: "0.75rem", padding: "0.25rem 0.5rem"}}
                        >
                          <i className="bi bi-exclamation-triangle me-1"></i>
                          {unavailableMedicines.length} Custom
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mb-3">
            <MedicineSearch onAdd={handleAdd} />
          </div>

          <div className="d-flex gap-2 mb-3">
            <button
              className={`btn btn-sm ${showLowStock ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setShowLowStock(!showLowStock)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16" className="me-1">
                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z"/>
              </svg>
              Low Stock {showLowStock ? '▼' : '▶'}
            </button>
          </div>

          {showLowStock && (
            <div className="card mb-3" style={{ 
              border: "1px solid #e0e0e0",
              borderRadius: "12px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
            }}>
              <div className="card-body p-3">
                <LowStockList key={refreshKey} onAdd={handleAdd} />
              </div>
            </div>
          )}

          <div className="card" style={{ 
            border: "1px solid #e0e0e0",
            borderRadius: "12px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
          }}>
            <div className="card-body p-3">
              <h6 className="mb-3 fw-semibold d-flex align-items-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16" className="me-2" style={{color: "#667eea"}}>
                  <path d="M2 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v13.5a.5.5 0 0 1-.777.416L8 13.101l-5.223 2.815A.5.5 0 0 1 2 15.5V2zm2-1a1 1 0 0 0-1 1v12.566l4.723-2.482a.5.5 0 0 1 .554 0L13 14.566V2a1 1 0 0 0-1-1H4z"/>
                  <path d="M10.854 5.146a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7.5 7.793l2.646-2.647a.5.5 0 0 1 .708 0z"/>
                </svg>
                Quick Add - Top Sellers
              </h6>
              
              {topMedicines.length === 0 ? (
                <div className="text-muted text-center py-3">No top selling items</div>
              ) : (
                <div className="row g-2">
                  {topMedicines.map((med) => (
                    <div key={med._id} className="col-md-3 col-sm-4 col-6">
                      <button
                        className="w-100 btn btn-outline-primary position-relative"
                        style={{
                          borderRadius: "8px",
                          padding: "0.75rem 0.5rem",
                          fontSize: "0.85rem",
                          textAlign: "left",
                          height: "100%"
                        }}
                        onClick={() => handleAdd(med)}
                      >
                        <div className="fw-semibold text-truncate">{med.name}</div>
                        <div className="d-flex justify-content-between align-items-center mt-1">
                          <small className={med.stock <= 5 ? "text-danger" : "text-muted"}>
                            Stock: {med.stock}
                          </small>
                          <small className="text-success fw-bold">
                            {med.price ? `Rs ${med.price}` : ''}
                          </small>
                        </div>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT SIDE - Cart */}
        <div className="col-lg-4" style={{ 
          height: "100%",
          background: "white",
          borderLeft: "1px solid #e0e0e0",
          display: "flex",
          flexDirection: "column"
        }}>
          
          <div style={{ 
            padding: "1rem 1.5rem",
            borderBottom: "2px solid #f0f0f0",
            background: "#fafafa"
          }}>
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0 fw-bold">Current Order</h5>
              <span className="badge bg-primary" style={{ fontSize: "0.9rem", padding: "0.4rem 0.8rem" }}>
                {totalQty} Items
              </span>
            </div>
          </div>

          <div style={{ 
            flex: 1,
            overflowY: "auto",
            padding: "1rem 1.5rem"
          }}>
            {cart.length === 0 ? (
              <div className="text-center py-5">
                <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="currentColor" viewBox="0 0 16 16" style={{color: "#ddd"}}>
                  <path d="M0 1.5A.5.5 0 0 1 .5 1H2a.5.5 0 0 1 .485.379L2.89 3H14.5a.5.5 0 0 1 .491.592l-1.5 8A.5.5 0 0 1 13 12H4a.5.5 0 0 1-.491-.408L2.01 3.607 1.61 2H.5a.5.5 0 0 1-.5-.5zM3.102 4l1.313 7h8.17l1.313-7H3.102zM5 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm7 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm-7 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm7 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
                </svg>
                <p className="text-muted mt-3">Cart is empty</p>
                <small className="text-muted">Search and add medicines to start</small>
              </div>
            ) : (
              cart.map((item) => (
                <div
                  key={item.medicine}
                  className="card mb-2"
                  style={{
                    border: item.isCustom ? "2px solid #ffc107" : "1px solid #e8e8e8",
                    borderRadius: "8px",
                    transition: "all 0.2s"
                  }}
                >
                  <div className="card-body p-2">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div style={{ flex: 1 }}>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <div className="fw-semibold" style={{ fontSize: "0.9rem" }}>
                            {item.name}
                          </div>
                          {item.isPrescribed && (
                            <span 
                              className="badge bg-success" 
                              style={{ 
                                fontSize: "0.65rem", 
                                cursor: "help",
                                whiteSpace: "nowrap"
                              }}
                              title={item.prescriptionDetails ? 
                                `Prescribed Details:\nDosage: ${item.prescriptionDetails.dosage}\nFrequency: ${item.prescriptionDetails.frequency}\nDuration: ${item.prescriptionDetails.duration}\nRoute: ${item.prescriptionDetails.route || 'N/A'}` : 
                                "Prescribed medicine from patient's medical record"
                              }
                            >
                              <i className="bi bi-prescription2 me-1"></i>
                              Prescribed
                            </span>
                          )}
                          {item.isCustom && (
                            <span className="badge bg-warning text-dark" style={{ fontSize: "0.65rem" }}>
                              <i className="bi bi-tag me-1"></i>
                              Custom
                            </span>
                          )}
                        </div>
                        {item.isOutOfStock && (
                          <span className="badge bg-danger" style={{ fontSize: "0.65rem" }}>
                            <i className="bi bi-exclamation-triangle me-1"></i>
                            Out of Stock
                          </span>
                        )}
                        {!item.isOutOfStock && item.stock <= 5 && (
                          <span className="badge bg-warning text-dark" style={{ fontSize: "0.65rem" }}>
                            Low Stock
                          </span>
                        )}
                      </div>
                      <button
                        className="btn btn-sm btn-light"
                        onClick={() => handleRemoveItem(item.medicine)}
                        style={{ 
                          padding: "0.2rem 0.4rem",
                          lineHeight: 1,
                          border: "none"
                        }}
                        title="Remove"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16" style={{color: "#dc3545"}}>
                          <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
                        </svg>
                      </button>
                    </div>
                    
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center gap-2">
                        <small className="text-muted">Qty:</small>
                        <input
                          type="number"
                          min={1}
                          value={item.quantity}
                          onChange={(e) =>
                            setCart((prev) =>
                              prev.map((c) =>
                                c.medicine === item.medicine
                                  ? { ...c, quantity: Math.max(1, Number(e.target.value)) }
                                  : c
                              )
                            )
                          }
                          className="form-control form-control-sm"
                          style={{ 
                            width: "55px",
                            textAlign: "center",
                            padding: "0.25rem"
                          }}
                        />
                      </div>
                      {item.isCustom ? (
                        <div className="d-flex align-items-center gap-2">
                          <small className="text-muted">Price:</small>
                          <input
                            type="number"
                            min={0}
                            step="0.01"
                            value={item.priceAtSale}
                            onChange={(e) =>
                              setCart((prev) =>
                                prev.map((c) =>
                                  c.medicine === item.medicine
                                    ? { ...c, priceAtSale: Math.max(0, Number(e.target.value)) }
                                    : c
                                )
                              )
                            }
                            className="form-control form-control-sm"
                            placeholder="Price"
                            style={{ 
                              width: "70px",
                              textAlign: "center",
                              padding: "0.25rem"
                            }}
                          />
                        </div>
                      ) : (
                        <div className="text-end">
                          <small className="text-muted d-block" style={{ fontSize: "0.7rem" }}>
                            Rs {item.priceAtSale} × {item.quantity}
                          </small>
                          <strong style={{ color: "#667eea", fontSize: "0.95rem" }}>
                            Rs {(item.priceAtSale * item.quantity).toFixed(2)}
                          </strong>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          <div style={{ 
            padding: "1rem 1.5rem",
            borderTop: "2px solid #f0f0f0",
            background: "white"
          }}>
            
            <div className="d-flex justify-content-between mb-2">
              <span className="text-muted">Subtotal</span>
              <strong>Rs {subTotal.toFixed(2)}</strong>
            </div>

            <div className="mb-2">
              <label className="form-label mb-1" style={{ fontSize: "0.85rem", color: "#666" }}>
                Discount (PKR)
              </label>
              <input
                className="form-control"
                type="number"
                min="0"
                placeholder="0.00"
                value={overallDiscount}
                onChange={handleDiscountChange}
                style={{
                  borderRadius: "8px",
                  border: "1px solid #e0e0e0"
                }}
              />
            </div>

            <div className="d-flex justify-content-between align-items-center mb-3 p-3" style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderRadius: "10px"
            }}>
              <span className="text-white fw-semibold">Total Amount</span>
              <strong className="text-white" style={{ fontSize: "1.5rem" }}>
                Rs {grandTotal.toFixed(2)}
              </strong>
            </div>

            <div className="d-grid gap-2">
              <button
                className="btn btn-success btn-lg fw-semibold"
                onClick={handleCheckout}
                disabled={cart.length === 0}
                style={{
                  borderRadius: "10px",
                  padding: "0.75rem",
                  fontSize: "1rem"
                }}
              >
                Complete Sale
              </button>
              <button
                className="btn btn-outline-secondary"
                onClick={() => {
                  setCart([]);
                  resetDiscount();
                  showToast({ type: "info", message: "Cart cleared" });
                }}
                style={{
                  borderRadius: "10px",
                  padding: "0.6rem"
                }}
              >
                Clear Cart
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SellMedicineScreen;